/**
 * Motor de facetas del catálogo público (`/courses`).
 *
 * Por qué existe este módulo y por qué no vive dentro del componente:
 *
 * 1. **Los controles los decide el DATO, no el diseño.** Antes la página pintaba cinco filtros
 *    fijos (buscador, categoría, precio, idioma, orden) sobre DOS cursos publicados. Medido en los
 *    tres ambientes: idioma no filtra nada (no hay un solo curso en inglés), orden no cambia nada
 *    (con un único precio distinto, "menor a mayor" y "mayor a menor" devuelven la misma lista) y
 *    categoría en prod tampoco (una sola categoría con contenido). O sea: cinco controles cuyo
 *    único resultado posible era vaciar la grilla, comiéndose el 77% de la primera pantalla en
 *    mobile antes de que asome el primer curso. Acá se calcula qué control tiene poder real de
 *    discriminar, y el que no lo tiene no se renderiza.
 *
 * 2. **Se puede testear sin montar React.** Lo importante de esta página es la lógica de match y
 *    de orden; el CSS no se testea.
 *
 * 3. **El filtrado se fue del servidor a acá a propósito.** El índice full-text se arma con
 *    `jsonb_to_tsvector('english', …)` y la query con `to_tsquery('spanish', …)`
 *    (apps/api/src/courses/utils/courses.utils.ts:8-9 vs course.service.ts:2471), así que buscar
 *    "inteligencia" o "artificial" devuelve CERO cursos con el curso llamándose exactamente
 *    "Introducción a la Inteligencia Artificial". Filtrar en cliente esquiva el bug en esta
 *    página; el bug del backend sigue vivo para el resto y tiene ticket aparte.
 *
 * 4. **La regla de precio NO se escribe acá.** Sale de `@repo/shared` (`isCourseFree` /
 *    `isCoursePaid` / `courseChargeableCents`). La primera versión de este archivo comparaba
 *    `priceInCents === 0` a mano en los tres lugares (facetas, filtro y orden) y era la 12ª copia de
 *    una regla que el encabezado de `packages/shared/src/utils/coursePricing.ts` declara duplicada en
 *    11 lugares del front "con desfasajes silenciosos". El desfasaje medido era éste: un curso con
 *    `priceInCents = 0` PERO con `mercadopagoProductId` (curso pago mal cargado) le salía "Comprar"
 *    en la tarjeta —que sí usa el helper— y "gratis" a las facetas, al filtro y al orden. O sea el
 *    chip "Gratis" mostraba un curso cuyo botón dice "Comprar", y "De pago" lo escondía.
 */
import { courseChargeableCents, isCourseFree, isCoursePaid } from "@repo/shared";

import type { CoursePricingInput } from "@repo/shared";

/**
 * Debajo de este umbral un buscador no ayuda: con 2-7 tarjetas a la vista, escanear con el ojo es
 * más rápido que tipear, y el input se lleva 44px del fold para nada.
 */
export const SEARCH_MIN_COURSES = 8;

/**
 * Techo del filtrado en cliente. Está atado al `perPage: 100` hardcodeado en
 * `apps/web/app/api/queries/useAvailableCourses.ts:35`: mientras el catálogo entre en una sola
 * página, filtrar y ordenar en cliente dice la verdad. Pasados los 100 cursos empezaría a
 * filtrar sólo la página cargada, o sea a mentir. La salida NO es un "modo servidor" dual
 * (un buscador que cambia de semántica según el tamaño del catálogo es el tipo de cosa que
 * después nadie recuerda): es paginación real, y es otra tarea. Acá sólo avisamos, fuerte.
 */
export const CLIENT_FILTER_MAX = 100;

export type PriceFilter = "all" | "free" | "paid";

export type CourseFilters = {
  searchQuery: string;
  category: string;
  priceFilter: PriceFilter;
};

export const initialFilters: CourseFilters = {
  searchQuery: "",
  category: "",
  priceFilter: "all",
};

export type CourseFacets = {
  /** Mostrar buscador */
  search: boolean;
  /** Categorías con al menos un curso publicado detrás, ordenadas alfabéticamente en es-AR */
  categories: string[];
  /** Mostrar chips de precio */
  price: boolean;
};

/**
 * `CoursePricingInput` y no `{ priceInCents: number }`: la regla de precio necesita los TRES campos
 * (`priceInCents`, `mercadopagoPriceInCents`, `mercadopagoProductId`), y con el tipo estrecho de
 * antes ni el compilador podía avisar que faltaba el que decide el caso borde.
 */
type FacetCourse = CoursePricingInput & { category?: string | null };
type QueryCourse = { title: string; description?: string | null };
type OrderCourse = CoursePricingInput & {
  title: string;
  /**
   * Único campo editorial que YA existe en la base (`courses.is_featured`, schema/index.ts:168) y se
   * edita desde el admin. El backend lo mantiene EXCLUSIVO: al marcar uno, desmarca todos los demás
   * (course.service.ts:1482-1486). O sea: es "el curso que encabeza la vidriera", exactamente lo que
   * hacía falta, sin migración.
   */
  isFeatured?: boolean;
  /** Volumen de contenido. Viene en el listado público; se usa como desempate estable. */
  courseLessonCount?: number;
  courseChapterCount?: number;
};

/** Saca tildes y baja a minúsculas: "Introducción" y "introduccion" tienen que matchear igual. */
export function foldAccents(value: string): string {
  return value
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();
}

/**
 * Slug tolerante. Hace falta porque el `?category=` de la URL lo escribe cualquiera:
 * `CategoryNavigationButtons` linkea con el TÍTULO de la categoría (es el único valor que el listado
 * público expone por curso — ver el comentario largo de ese archivo), pero a mano alguien puede
 * escribir "ia-practica". Comparando los dos slugificados, las dos formas caen en el mismo curso.
 *
 * OJO con lo que esto NO puede: el slug REAL de la categoría en la base es inglés (`ai-practical`),
 * y el listado público no lo devuelve, así que ninguna cantidad de slugificado hace matchear
 * "ai-practical" con "IA Práctica". Eso lo maneja `resolveCategory` cayendo a "sin filtro".
 */
export function slugify(value: string): string {
  return foldAccents(value)
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/** La descripción viene como HTML del admin; para buscar sólo nos interesa el texto. */
export function stripHtml(value: string): string {
  return value.replace(/<[^>]*>/g, " ");
}

/**
 * Las facetas se calculan SIEMPRE sobre el set sin filtrar. Si se derivaran del set ya filtrado,
 * el control desaparecería justo cuando el usuario se metió en un rincón vacío y necesita el
 * control para salir.
 */
export function deriveFacets(courses: readonly FacetCourse[]): CourseFacets {
  const categories = [...new Set(courses.map((c) => c.category).filter((c): c is string => !!c))];
  categories.sort((a, b) => a.localeCompare(b, "es"));

  return {
    search: courses.length >= SEARCH_MIN_COURSES,
    categories,
    // Un chip "Gratis" que devuelve todo y un "De pago" que devuelve nada no es un filtro: es un
    // botón para vaciar la pantalla. Hace falta que existan las dos poblaciones, y "gratis" tiene
    // que querer decir lo MISMO que en la tarjeta: por eso el helper compartido y no `=== 0`.
    price: courses.some(isCourseFree) && courses.some(isCoursePaid),
  };
}

/**
 * Traduce el `?category=` de la URL a un valor que el filtro entienda, o a "" si no hay con qué.
 *
 * ═══ Por qué esta función existe (era un fold roto, no una hipótesis) ═══
 *
 * `Courses.page.tsx` sembraba el filtro de categoría directo con `searchParams.get("category")`. Si
 * ese valor no matcheaba ninguna categoría real, el PRIMER render ya salía con la grilla vacía y el
 * aviso de "ninguno coincide" empujando la primera tarjeta a 469/496/537px (390×844 / 360×740 /
 * 1440×900) contra un techo de 300 — sin una sola interacción del usuario.
 *
 * Y el camino estaba VIVO: `CategoryNavigationButtons` linkeaba con el TÍTULO de la categoría
 * mientras el filtro compara contra el valor que traen los cursos. Cualquier categoría con página y
 * sin cursos publicados, o renombrada, generaba un botón público que aterrizaba en ese estado.
 *
 * La normalización va acá y no en un `useEffect` a propósito: es derivación pura, así que el estado
 * roto no existe NI POR UN RENDER (un efecto normalizaría después de pintar, o sea después de medir),
 * y funciona igual en el servidor, que es donde arranca esta página.
 *
 * Efecto lateral buscado: mientras el catálogo carga, `categories` está vacío y esto devuelve "" —
 * o sea el fold nunca depende de la query string, con cualquier query string.
 */
export function resolveCategory(raw: string, categories: readonly string[]): string {
  if (raw === "") return "";
  const wanted = slugify(raw);
  return categories.find((c) => c === raw || slugify(c) === wanted) ?? "";
}

export function matchesQuery(course: QueryCourse, query: string): boolean {
  const needle = foldAccents(query);
  if (needle === "") return true;

  const title = foldAccents(course.title ?? "");
  if (title.includes(needle)) return true;

  const description = foldAccents(stripHtml(course.description ?? ""));
  return description.includes(needle);
}

export function applyFilters<T extends FacetCourse & QueryCourse>(
  courses: readonly T[],
  filters: CourseFilters,
): T[] {
  let result = [...courses];

  if (filters.category !== "") {
    const wanted = slugify(filters.category);
    result = result.filter(
      (c) => c.category === filters.category || (!!c.category && slugify(c.category) === wanted),
    );
  }

  // Los mismos helpers que usa la tarjeta para elegir entre "Inscribirme gratis" y "Comprar": un
  // filtro que reparta los cursos distinto que el botón de cada tarjeta es un filtro que miente.
  if (filters.priceFilter === "free") {
    result = result.filter(isCourseFree);
  } else if (filters.priceFilter === "paid") {
    result = result.filter(isCoursePaid);
  }

  if (filters.searchQuery !== "") {
    result = result.filter((c) => matchesQuery(c, filters.searchQuery));
  }

  return result;
}

/**
 * Orden único, como regla de negocio y no como control de UI.
 *
 * Reemplaza al select de "Ordenar por" que tenía cuatro opciones para ordenar dos filas. Se
 * eliminó además la opción "Más recientes": la API viva NO devuelve `createdAt` en el listado
 * público (verificado contra staging: la clave no viene en la respuesta, aunque el tipo generado la
 * declare opcional), así que ese comparador restaba `0 - 0` para todo par y no ordenaba nada.
 *
 * ═══ Por qué el desempate NO puede ser el título ═══
 *
 * La versión anterior era: gratis → precio → `title.localeCompare(es)`. Con el catálogo de staging
 * los dos cursos son gratis y valen 0, así que la vidriera la encabezaba el que ganaba el
 * abecedario: "IA para tu trabajo" (el de empleabilidad) por delante del curso de introducción, que
 * es la puerta de entrada del embudo. En prod salía bien de pura casualidad, porque el otro es pago.
 * Que la primera tarjeta del catálogo la decida la letra inicial de un título es azar, no criterio.
 *
 * Las cuatro claves, en orden, y de dónde sale cada una:
 * 1. `isFeatured` — la intención editorial explícita del admin. Es el único campo de este tipo que
 *    existe hoy en la base y el backend lo mantiene exclusivo, así que manda por encima de todo:
 *    si alguien marcó un curso como destacado, ése encabeza, gratis o pago.
 * 2. Gratis primero — es la puerta de entrada del catálogo y no hay fricción de compra.
 * 3. Precio de menor a mayor.
 * 4. A IGUAL precio, más contenido primero (clases, y capítulos como segundo desempate): el curso
 *    más completo es la pieza más fuerte de la vidriera. Y es estable — el volumen de contenido de
 *    un curso publicado no se mueve de un deploy al otro, a diferencia de
 *    `enrolledParticipantCount`, que hoy vale -7 en staging (el contador de inscriptos quedó
 *    negativo al borrar inscripciones) y por eso NO se usa.
 * 5. El título, sólo como último recurso — no para "ordenar alfabéticamente" sino para que la
 *    función sea total y determinista con cualquier dato.
 *
 * ═══ Cuál de las cinco claves decide HOY, medido ═══
 *
 * `is_featured` es `f` en las SEIS filas de `courses` en staging (verificado por SQL el 2026-07-26),
 * y las dos publicadas valen las dos 0. O sea: hoy la clave 1 no discrimina, la 2 tampoco y la 3
 * tampoco — la que efectivamente encabeza la vidriera es el DESEMPATE POR CANTIDAD DE CLASES (16 vs
 * 4). `isFeatured` es la palanca que existe para cuando alguien la use desde el admin, no la razón
 * del orden actual. Si un resumen dice "ahora ordena por destacado", el resumen miente.
 *
 * OJO: esto ordena, no PROMETE. No hay ningún campo que diga "éste es el curso de entrada del
 * embudo", así que la página no rotula la primera tarjeta con un "empezá acá" (ver el comentario en
 * `Courses.page.tsx`).
 */
export function defaultOrder<T extends OrderCourse>(courses: readonly T[]): T[] {
  return [...courses].sort((a, b) => {
    const featured = Number(!!b.isFeatured) - Number(!!a.isFeatured);
    if (featured !== 0) return featured;

    // Gratis / pago con el helper compartido, no con `priceInCents === 0`: si no, el curso pago mal
    // cargado (precio 0 + `mercadopagoProductId`) se ordenaba entre los gratis mientras su tarjeta
    // decía "Comprar".
    const aFree = isCourseFree(a) ? 0 : 1;
    const bFree = isCourseFree(b) ? 0 : 1;
    if (aFree !== bFree) return aFree - bFree;

    // `courseChargeableCents` con "ARS" fijo: es lo que la tarjeta IMPRIME (elige el precio de
    // MercadoPago cuando está configurado y cae al base cuando no), así que el orden coincide con los
    // números que el usuario ve. El "ARS" no es una suposición: `currency` es `ars` en las 6 filas de
    // `courses` de staging, el catálogo es de un solo mercado. Si algún día hay dos monedas, esta
    // línea deja de servir y hay que ordenar por precio normalizado, no por centavos crudos.
    const aPrice = courseChargeableCents(a, "ARS");
    const bPrice = courseChargeableCents(b, "ARS");
    if (aPrice !== bPrice) return aPrice - bPrice;

    const lessons = (b.courseLessonCount ?? 0) - (a.courseLessonCount ?? 0);
    if (lessons !== 0) return lessons;

    const chapters = (b.courseChapterCount ?? 0) - (a.courseChapterCount ?? 0);
    if (chapters !== 0) return chapters;

    return a.title.localeCompare(b.title, "es");
  });
}

export function isFilterActive(filters: CourseFilters): boolean {
  return filters.searchQuery !== "" || filters.category !== "" || filters.priceFilter !== "all";
}
